import { useState, useEffect } from 'react';

/**
 * Custom hook to ensure skeleton loading states are shown for a minimum duration
 * @param {boolean} isLoading - The actual loading state from React Query
 * @param {number} minimumTime - Minimum time in milliseconds to show loading (default: 1000ms)
 * @returns {boolean} - Extended loading state that shows for at least the minimum time
 */
const useMinimumLoadingTime = (isLoading, minimumTime = 1000) => {
  const [isShowingLoading, setIsShowingLoading] = useState(isLoading);
  const [startTime, setStartTime] = useState(null);

  useEffect(() => {
    if (isLoading && startTime === null) {
      // Start loading - record the start time
      setStartTime(Date.now());
      setIsShowingLoading(true);
    } else if (!isLoading && startTime !== null) {
      // Loading finished - check if minimum time has passed
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, minimumTime - elapsed);

      if (remainingTime > 0) {
        // Wait for the remaining time before hiding skeleton
        const timer = setTimeout(() => {
          setIsShowingLoading(false);
          setStartTime(null);
        }, remainingTime);

        return () => clearTimeout(timer);
      } else {
        // Minimum time already passed, hide immediately
        setIsShowingLoading(false);
        setStartTime(null);
      }
    }
  }, [isLoading, startTime, minimumTime]);

  return isShowingLoading;
};

export default useMinimumLoadingTime;